import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

const CATEGORIES = ['All', 'Shelter', 'Food', 'Healthcare', 'Clothing', 'Legal', 'Crisis'];

const ResourceDirectory = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('All');

  const handleCopyPhone = (id, phone) => {
    navigator.clipboard.writeText(phone);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getVerificationStatus = (lastVerified) => {
    if (!lastVerified) return 'pending';
    const verifiedDate = lastVerified.toDate ? lastVerified.toDate() : new Date(lastVerified);
    const diffTime = Math.abs(new Date() - verifiedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 90 ? 'pending' : 'verified';
  };

  useEffect(() => {
    // Reads the PUBLIC projection, not the staff-only /resources collection.
    // /resources carries internal staff notes and is deliberately unreadable
    // by the public; publicResources is an allowlisted copy maintained by the
    // syncPublicResource Cloud Function.
    const q = query(
      collection(db, 'publicResources'),
      where('active', '==', true),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setResources(docs);
        setLoadFailed(false);
        setLoading(false);
      },
      (error) => {
        // Previously this failed silently and the directory rendered as
        // "no resources match your filter", which is a different and
        // misleading claim. Surface the real state and keep a way forward.
        console.error('Error fetching public resources:', error);
        setLoadFailed(true);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredResources = resources.filter((res) => {
    const matchesCategory =
      selectedCategory === 'All' ||
      (res.categories &&
        res.categories.some(
          (c) => c.toLowerCase() === selectedCategory.toLowerCase()
        ));

    const matchesRegion =
      selectedRegion === 'All' ||
      !res.serviceArea ||
      res.serviceArea.toLowerCase().includes(selectedRegion.toLowerCase());

    const matchesSearch =
      !searchQuery.trim() ||
      res.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      res.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      res.eligibility?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesRegion && matchesSearch;
  });

  return (
    <section className="chrome-panel border border-white/5 rounded-lg p-6 md:p-8 my-8 relative z-10" aria-label="Community Resource Directory">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="w-2.5 h-6 bg-accent-purple block rounded-sm shadow-[0_0_10px_#8E2DE2]"></span>
            <h2 className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white font-display">
              Community Resource Directory
            </h2>
          </div>
          <p className="text-xs text-slate-400">
            Verified local services for shelter, food, healthcare, and crisis support in Louisville metro.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          {/* Region Select */}
          <div className="w-full sm:w-36">
            <label htmlFor="region-select" className="sr-only">
              Filter by Region
            </label>
            <select
              id="region-select"
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full px-3 py-1.5 text-xs bg-charcoal-card border border-white/10 rounded text-white focus:ring-2 focus:ring-accent-purple"
            >
              <option value="All">All Regions</option>
              <option value="Louisville">Louisville</option>
              <option value="Shepherdsville">Shepherdsville</option>
              <option value="Mt. Washington">Mt. Washington</option>
              <option value="Elizabethtown">Elizabethtown</option>
            </select>
          </div>

          {/* Search input */}
          <div className="w-full sm:w-48">
            <label htmlFor="resource-search" className="sr-only">
              Search resources
            </label>
            <input
              id="resource-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resources..."
              className="w-full px-3 py-1.5 text-xs bg-charcoal-card border border-white/10 rounded text-white placeholder-slate-500 focus:ring-2 focus:ring-accent-purple"
            />
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Resource categories">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            role="tab"
            aria-selected={selectedCategory === cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1 text-xs font-bold uppercase tracking-wider rounded transition-all ${
              selectedCategory === cat
                ? 'bg-accent-purple text-white shadow-[0_0_10px_rgba(142,45,226,0.4)]'
                : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Results Count & Clear Filters */}
      {!loading && (
        <div className="flex items-center gap-2 mb-4 text-xs text-slate-500">
          <span>
            Showing {filteredResources.length} of {resources.length} resource{resources.length !== 1 ? 's' : ''}
          </span>
          {(selectedCategory !== 'All' || selectedRegion !== 'All' || searchQuery.trim()) && (
            <button
              type="button"
              onClick={() => {
                setSelectedCategory('All');
                setSelectedRegion('All');
                setSearchQuery('');
              }}
              className="text-banana-yellow hover:underline cursor-pointer font-bold uppercase tracking-wider"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Resource Cards Grid */}
      {loading ? (
        <p className="text-xs text-slate-400 py-4 text-center">Loading local resources...</p>
      ) : loadFailed ? (
        <div className="text-center py-6 border border-white/5 rounded bg-charcoal-card/40" role="alert">
          <p className="text-xs text-slate-300">
            We could not load the resource list right now.
          </p>
          <p className="text-[11px] text-slate-400 mt-1">
            Call <a href="tel:211" className="text-banana-yellow font-bold hover:underline">211</a> for
            free, 24/7 help finding local services, or{' '}
            <a href="tel:988" className="text-banana-yellow font-bold hover:underline">988</a> if you are
            in crisis. In an emergency, call{' '}
            <a href="tel:911" className="text-banana-yellow font-bold hover:underline">911</a>.
          </p>
        </div>
      ) : filteredResources.length === 0 ? (
        <div className="text-center py-6 border border-white/5 rounded bg-charcoal-card/40">
          <p className="text-xs text-slate-400">No resources match your search or filter.</p>
          <p className="text-[11px] text-slate-500 mt-1">
            Need emergency assistance? Call <a href="tel:211" className="text-banana-yellow font-bold hover:underline">211</a> anytime.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredResources.map((res) => (
            <div
              key={res.id}
              className="bg-charcoal-card/60 border border-white/5 rounded-lg p-4 hover:border-accent-purple/30 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex flex-col items-start gap-0.5">
                    <h3 className="font-bold text-white text-sm uppercase tracking-wide flex items-center gap-1.5">
                      {res.name}
                      {getVerificationStatus(res.lastVerified) === 'verified' ? (
                        <span className="text-green-400 text-xs" title="Verified within past 90 days" aria-label="Verified organization">✓</span>
                      ) : (
                        <span className="text-yellow-500 text-xs" title="Verification pending / older than 90 days" aria-label="Verification pending">⚠️</span>
                      )}
                    </h3>
                    {res.lastVerified && (
                      <span className="text-3xs text-slate-500 uppercase tracking-wider font-semibold">
                        Verified: {res.lastVerified.toDate ? res.lastVerified.toDate().toLocaleDateString() : new Date(res.lastVerified).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {res.categories && res.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {res.categories.map((c) => (
                        <span
                          key={c}
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-slate-300 border border-white/5"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {res.description && (
                  <p className="text-xs text-slate-300 mb-3 leading-relaxed">
                    {res.description}
                  </p>
                )}

                <div className="space-y-1 text-xs text-slate-400 mb-3">
                  {res.hours && (
                    <p>
                      <strong className="text-slate-300">Hours:</strong> {res.hours}
                    </p>
                  )}
                  {res.eligibility && (
                    <p>
                      <strong className="text-slate-300">Eligibility:</strong> {res.eligibility}
                    </p>
                  )}
                  {res.serviceArea && (
                    <p>
                      <strong className="text-slate-300">Area:</strong> {res.serviceArea}
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-3 border-t border-white/5 flex items-center justify-between text-xs font-bold uppercase">
                {res.phone ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={`tel:${res.phone}`}
                      className="text-banana-yellow hover:underline flex items-center gap-1"
                    >
                      📞 {res.phone}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopyPhone(res.id, res.phone)}
                      className="text-[10px] text-slate-400 hover:text-white uppercase font-normal tracking-wider bg-white/5 px-1.5 py-0.5 rounded border border-white/5 transition-colors"
                      title="Copy phone number to clipboard"
                    >
                      {copiedId === res.id ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                ) : (
                  <span />
                )}
                {res.website && (
                  <a
                    href={res.website.startsWith('http') ? res.website : `https://${res.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-cyan hover:underline"
                  >
                    Visit Website ↗
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ResourceDirectory;
